// Projets immobiliers — conditions du marché (Nils 29/10) : avance, retenue de garantie, compte prorata.
// Sur chaque facture d'un dossier du projet :
//   Total HT des travaux
//   − remboursement de l'avance (même % que l'avance, sans dépasser le solde ; solde exact sur la
//     dernière facture) — la TVA de l'avance est déjà sur la facture d'avance ;
//   − retenue de garantie (% du HT des travaux) — facturée en fin de projet, avec sa TVA ;
//   − compte prorata (% du HT après avance et retenue), Nils 30/10 (accord du comptable) : avant la TVA ;
//   = HT net facturé → TVA → TTC = net à payer.
// Les factures émises avant le 30/10 gardent leur calcul figé (prorata déduit du TTC, sans le repère
// « prorataAvantTVA ») : voir prorataRegleParClient.

const arrondi = (x) => Math.round((Number(x) || 0) * 100) / 100;
const pct = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return isFinite(n) && n > 0 ? n : 0;
};

export const RETENUE_DEFAUT = 10;
export const PRORATA_DEFAUT = 2;

// Conditions d'un projet (null si aucune).
export function conditionsMarche(projet) {
  const m = projet && projet.marche;
  if (!m) return null;
  const c = { avancePct: pct(m.avancePct), retenuePct: pct(m.retenuePct), prorataPct: pct(m.prorataPct) };
  return c.avancePct || c.retenuePct || c.prorataPct ? c : null;
}

// htTravaux, tauxTVA (0 si exonéré), conditions, soldeAvance (HT restant à rembourser), solder (dernière facture).
export function deductionsFacture({ htTravaux, tauxTVA, conditions, soldeAvance = 0, solder = false }) {
  const ht = arrondi(htTravaux);
  const c = conditions || { avancePct: 0, retenuePct: 0, prorataPct: 0 };
  const solde = Math.max(0, arrondi(soldeAvance));
  const retenue = arrondi((ht * c.retenuePct) / 100);
  const parPct = arrondi((ht * c.avancePct) / 100);
  const avance = Math.min(solder ? solde : Math.min(parPct, solde), Math.max(0, arrondi(ht - retenue)));
  const htAvantProrata = arrondi(ht - avance - retenue);
  const prorata = arrondi((htAvantProrata * c.prorataPct) / 100);
  const htNet = arrondi(htAvantProrata - prorata);
  const tva = arrondi(htNet * (Number(tauxTVA) || 0));
  const ttc = arrondi(htNet + tva);
  return { htTravaux: ht, avancePct: c.avancePct, avance, retenuePct: c.retenuePct, retenue, prorataPct: c.prorataPct, prorata, prorataAvantTVA: true, htNet, tauxTVA: Number(tauxTVA) || 0, tva, ttc, net: ttc };
}

// Part du compte prorata « réglée » par le client sur son paiement : seulement pour les factures
// émises avant le 30/10 (prorata déduit après la TVA). Depuis, il est déduit du HT : rien à régler.
export function prorataRegleParClient(d) {
  return d && !d.prorataAvantTVA ? Number(d.prorata) || 0 : 0;
}

// Plusieurs dossiers sur une même facture (facture groupée) : totaux.
export function sommeDeductions(liste) {
  const champs = ["htTravaux", "avance", "retenue", "htNet", "tva", "ttc", "prorata", "net"];
  const l = (liste || []).filter(Boolean);
  if (!l.length) return null;
  const s = Object.fromEntries(champs.map((k) => [k, arrondi(l.reduce((n, d) => n + (Number(d[k]) || 0), 0))]));
  return { ...s, avancePct: l[0].avancePct, retenuePct: l[0].retenuePct, prorataPct: l[0].prorataPct, tauxTVA: l[0].tauxTVA, prorataAvantTVA: l.every((d) => d.prorataAvantTVA) };
}

// Facture d'avance ou de retenue : montant HT − compte prorata (% du HT) = HT net → TVA → TTC = net à payer.
// Même forme que les déductions d'une facture (PDF, TVA, règlements).
export function montantsFactureAvance({ ht, tauxTVA, prorataPct }) {
  const h = arrondi(ht);
  const p = pct(prorataPct);
  const prorata = arrondi((h * p) / 100);
  const htNet = arrondi(h - prorata);
  const tva = arrondi(htNet * (Number(tauxTVA) || 0));
  const ttc = arrondi(htNet + tva);
  return { htTravaux: h, avancePct: 0, avance: 0, retenuePct: 0, retenue: 0, prorataPct: p, prorata, prorataAvantTVA: true, htNet, tauxTVA: Number(tauxTVA) || 0, tva, ttc, net: ttc };
}

// Nils 03/11 : validation du dessin par le client — pour un devis rattaché à un projet immobilier,
// « Composition type validée » remplace l'accord du client (la composition type du projet est déjà validée).
export const MOYEN_COMPOSITION_TYPE = "Composition type validée";
export function moyensValidationDessin(avecProjet) {
  return ["Mail", "WhatsApp", "Signature", ...(avecProjet ? [MOYEN_COMPOSITION_TYPE] : [])];
}
